/**
 * Shared API client utilities for making authenticated requests.
 */
import { fetchAuthSession } from 'aws-amplify/auth';

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

/**
 * Get the ID token for API Gateway Cognito authorizer.
 */
export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Get headers for authenticated API requests.
 * Uses the ID token for API Gateway Cognito authorizer.
 */
export async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getIdToken();

  // API Gateway Cognito authorizer expects the raw token, not "Bearer <token>"
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: token } : {}),
  };
}

/**
 * Make an authenticated fetch request.
 */
export async function authFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const headers = await getAuthHeaders();
  return fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });
}

/**
 * Build a URL with query parameters.
 */
export function buildApiUrl(path: string, params?: URLSearchParams): string {
  const queryString = params?.toString();
  const basePath = `${API_BASE_URL}${path}`;
  return queryString ? `${basePath}?${queryString}` : basePath;
}
